import { useEffect, useState } from "react";

import ConfigService from "../services/config.service";
import localStorageService from "../services/localStorage.service";

const CACHE_TTL_MS = 10 * 60 * 1000;

// Module-level so the three config modals (client-side tagging, server-side
// tagging, tag volume alerting) share one request. Each mounts independently
// and reopening a modal must not refire the BigQuery-backed endpoint.
const cache = new Map();

const EMPTY = { clientTags: [], serverTags: [] };

const normalise = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .filter((r) => r && r.tag_name)
    .map((r) => ({ tag_name: r.tag_name, tag_id: r.tag_id || "" }));

const load = (companyId) => {
  const hit = cache.get(companyId);
  if (hit && Date.now() - hit.ts < CACHE_TTL_MS) return hit.promise;

  const promise = ConfigService.getTagNames(companyId)
    .then((resp) => resp.json())
    .then((data) => {
      if (!data?.result) return EMPTY;
      return {
        clientTags: normalise(data.client_tags),
        serverTags: normalise(data.server_tags),
      };
    });

  // Don't poison the cache with a rejected promise — a transient failure
  // should be retried the next time a modal opens.
  promise.catch(() => cache.delete(companyId));

  cache.set(companyId, { ts: Date.now(), promise });
  return promise;
};

/**
 * The distinct tag names/IDs this company has fired recently, split by side.
 *
 * Powers the tag-name autocomplete. Failure is deliberately non-fatal: the
 * fields fall back to plain free-text entry, which is exactly how they behaved
 * before suggestions existed.
 */
export const useTagNameOptions = (companyIdOverride) => {
  const companyId = companyIdOverride || localStorageService.getCompanyId();
  const [state, setState] = useState({ ...EMPTY, loading: true, error: false });

  useEffect(() => {
    if (!companyId || isNaN(companyId)) {
      setState({ ...EMPTY, loading: false, error: true });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: false }));

    load(companyId)
      .then((tags) => {
        if (cancelled) return;
        setState({ ...tags, loading: false, error: false });
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("Failed to load tag names:", e);
        setState({ ...EMPTY, loading: false, error: true });
      });

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return state;
};

export default useTagNameOptions;
